import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaUpload, FaTrash, FaEye, FaFileAlt, FaArrowLeft, FaTimes, FaGripLines, FaClipboardList } from 'react-icons/fa';
import './Admin.css';

// Template assessment data structure based on JSON files in the data directory
const mockAssessmentData = {
  // Sample questions based on the structure of the JSON files
  'Universal': [
    {
      'Category': 'Governance & Strategy',
      'Assessment Question': 'Is an enterprise resilience policy approved by the board?',
      'Level 0': 'No evidence or capability exists to ensure that enterprise resilience policy approved by the board.',
      'Level 1': 'Enterprise resilience policy approved by the board occurs sporadically and informally without documentation or consistency.',
      'Level 2': 'Enterprise resilience policy approved by the board is documented and repeatable in some areas but lacks enterprise-wide adoption.',
      'Level 3': 'Enterprise resilience policy approved by the board is consistently performed, tracked, and resourced across most business units and sites.',
      'Level 4': 'Enterprise resilience policy approved by the board fully meets leading practice and is regularly audited for effectiveness.',
      'Level 5': 'Enterprise resilience policy approved by the board is continuously improved, benchmarked, and embedded in strategic decision-making.'
    },
    {
      'Category': 'Governance & Strategy',
      'Assessment Question': 'Are clear governance structures in place for continuity oversight?',
      'Level 0': 'No evidence or capability exists to ensure that clear governance structures in place for continuity oversight.',
      'Level 1': 'Clear governance structures in place for continuity oversight occurs sporadically and informally without documentation or consistency.',
      'Level 2': 'Clear governance structures in place for continuity oversight is documented and repeatable in some areas but lacks enterprise-wide adoption.',
      'Level 3': 'Clear governance structures in place for continuity oversight is consistently performed, tracked, and resourced across most business units and sites.',
      'Level 4': 'Clear governance structures in place for continuity oversight fully meets leading practice and is regularly audited for effectiveness.',
      'Level 5': 'Clear governance structures in place for continuity oversight is continuously improved, benchmarked, and embedded in strategic decision-making.'
    },
    {
      'Category': 'Risk Assessment',
      'Assessment Question': 'Is a formal risk assessment process in place?',
      'Level 0': 'No evidence or capability exists to ensure that a formal risk assessment process is in place.',
      'Level 1': 'Formal risk assessment process occurs sporadically and informally without documentation or consistency.',
      'Level 2': 'Formal risk assessment process is documented and repeatable in some areas but lacks enterprise-wide adoption.',
      'Level 3': 'Formal risk assessment process is consistently performed, tracked, and resourced across most business units and sites.',
      'Level 4': 'Formal risk assessment process fully meets leading practice and is regularly audited for effectiveness.',
      'Level 5': 'Formal risk assessment process is continuously improved, benchmarked, and embedded in strategic decision-making.'
    }
  ],
  'Financial Assessment': [
    {
      'Category': 'Financial Controls',
      'Assessment Question': 'Are financial controls and compliance measures in place?',
      'Level 0': 'No evidence or capability exists for financial controls and compliance.',
      'Level 1': 'Basic financial controls exist but are inconsistently applied.',
      'Level 2': 'Financial controls are documented and repeatable but lack comprehensive coverage.',
      'Level 3': 'Financial controls are consistently performed and tracked across the organization.',
      'Level 4': 'Financial controls meet leading practice and are regularly audited.',
      'Level 5': 'Financial controls are continuously improved and embedded in strategic decision-making.'
    },
    {
      'Category': 'Risk Management',
      'Assessment Question': 'Is there a comprehensive approach to financial risk management?',
      'Level 0': 'No evidence of financial risk management exists.',
      'Level 1': 'Financial risk management is ad-hoc and reactive.',
      'Level 2': 'Financial risk management processes are documented but not consistently applied.',
      'Level 3': 'Financial risk management is consistently performed across the organization.',
      'Level 4': 'Financial risk management meets leading practice and includes proactive measures.',
      'Level 5': 'Financial risk management is continuously improved and integrated with business strategy.'
    }
  ],
  'Healthcare': [
    {
      'Category': 'Patient Data Security',
      'Assessment Question': 'How effective are patient data protection measures?',
      'Level 0': 'No evidence of patient data protection exists.',
      'Level 1': 'Basic patient data protection exists but is inconsistently applied.',
      'Level 2': 'Patient data protection is documented but lacks comprehensive coverage.',
      'Level 3': 'Patient data protection is consistently performed across the organization.',
      'Level 4': 'Patient data protection meets leading practice and includes proactive measures.',
      'Level 5': 'Patient data protection is continuously improved and integrated with business strategy.'
    },
    {
      'Category': 'Business Continuity',
      'Assessment Question': 'Are healthcare service continuity plans in place?',
      'Level 0': 'No evidence of healthcare service continuity planning exists.',
      'Level 1': 'Basic continuity planning exists but is inconsistently applied.',
      'Level 2': 'Continuity planning is documented but lacks comprehensive coverage.',
      'Level 3': 'Continuity planning is consistently performed across the organization.',
      'Level 4': 'Continuity planning meets leading practice and includes regular testing.',
      'Level 5': 'Continuity planning is continuously improved and integrated with business strategy.'
    }
  ]
};

// Add aliases for industry types
const assessmentData = {
  'Universal': mockAssessmentData['Universal'],
  'Financial Assessment': mockAssessmentData['Financial Assessment'],
  'Banking & Financial Services': mockAssessmentData['Financial Assessment'],
  'Healthcare': mockAssessmentData['Healthcare'],
  'Manufacturing': mockAssessmentData['Universal'],
  'Government': mockAssessmentData['Universal'],
  'Energy & Utilities': mockAssessmentData['Universal'],
  'Aviation': mockAssessmentData['Universal'],
  'Education': mockAssessmentData['Universal'],
  'Entertainment': mockAssessmentData['Universal'],
  'Information Technology': mockAssessmentData['Universal'],
  'Information Security': mockAssessmentData['Universal']
};

const TemplateManagement = () => {
  // Industry types list for dropdown
  const industryTypes = [
    "Aerospace & Defense",
    "Agriculture",
    "Automotive",
    "Aviation",
    "Banking & Financial Services",
    "Biotechnology",
    "Construction",
    "Consulting",
    "Consumer Goods",
    "Education",
    "Energy & Utilities",
    "Entertainment",
    "Food & Beverage",
    "Government",
    "Healthcare",
    "Hospitality & Tourism",
    "Information Technology",
    "Insurance",
    "Legal Services",
    "Manufacturing",
    "Media & Communications",
    "Mining & Metals",
    "Non-Profit",
    "Oil & Gas",
    "Pharmaceuticals",
    "Real Estate",
    "Retail",
    "Telecommunications",
    "Transportation & Logistics",
    "Universal",
    "Other"
  ];

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    industryType: '',
    templateName: '',
    templateData: null
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const modalRef = useRef(null);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/templates');
      setTemplates(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setUploadForm({
      ...uploadForm,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const jsonData = JSON.parse(event.target.result);
        setUploadForm({
          ...uploadForm,
          templateData: jsonData
        });
        setUploadMessage({
          type: 'success',
          text: `File loaded successfully: ${jsonData.length} questions found.`
        });
      } catch (err) {
        console.error('Error parsing JSON:', err);
        setUploadMessage({
          type: 'error',
          text: 'Invalid JSON file. Please check the file format.'
        });
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.industryType || !uploadForm.templateName || !uploadForm.templateData) {
      setUploadMessage({
        type: 'error',
        text: 'Please provide industry type, template name, and a valid template file.'
      });
      return;
    }
    
    try {
      setIsUploading(true);
      const res = await axios.post('/api/admin/templates/upload', uploadForm);
      setUploadMessage({
        type: 'success',
        text: res.data.msg
      });
      fetchTemplates(); // Refresh the template list
      
      // Reset form
      setUploadForm({
        industryType: '',
        templateName: '',
        templateData: null
      });
      
      // Reset file input
      const fileInput = document.getElementById('templateFile');
      if (fileInput) fileInput.value = '';
      
      setIsUploading(false);
    } catch (err) {
      console.error('Error uploading template:', err);
      setUploadMessage({
        type: 'error',
        text: err.response?.data?.msg || 'Failed to upload template. Please try again.'
      });
      setIsUploading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      try {
        await axios.delete(`/api/admin/templates/${templateId}`);
        setTemplates(templates.filter(template => template._id !== templateId));
      } catch (err) {
        console.error('Error deleting template:', err);
        alert('Failed to delete template. Please try again.');
      }
    }
  };

  const handleViewTemplate = async (templateId) => {
    try {
      setLoading(true);
      let templateData = null;
      
      try {
        // Try to get template from API
        const res = await axios.get(`/api/admin/templates/${templateId}`);
        templateData = res.data;
      } catch (apiErr) {
        console.log('Using mock assessment data');
        
        // Find the template in the list to use as fallback
        const template = templates.find(t => t._id === templateId);
        
        if (template) {
          // Get the appropriate assessment data based on industry type
          const industryType = template.industryType;
          const jsonData = assessmentData[industryType] || assessmentData['Universal'];
          
          // Process the JSON data into the expected format
          const questions = jsonData.map((item) => {
            // Extract options from Level 0-5
            const options = [];
            for (let i = 0; i <= 5; i++) {
              if (item[`Level ${i}`]) {
                options.push({
                  text: item[`Level ${i}`],
                  value: i
                });
              }
            }
            
            return {
              text: item['Assessment Question'],
              category: item['Category'],
              options: options
            };
          });
          
          templateData = {
            ...template,
            questions: questions,
            questionCount: questions.length
          };
        } else {
          // If we can't find the template in the list, use the Universal assessment
          const questions = mockAssessmentData['Universal'].map((item) => {
            // Extract options from Level 0-5
            const options = [];
            for (let i = 0; i <= 5; i++) {
              if (item[`Level ${i}`]) {
                options.push({
                  text: item[`Level ${i}`],
                  value: i
                });
              }
            }
            
            return {
              text: item['Assessment Question'],
              category: item['Category'],
              options: options
            };
          });
          
          templateData = {
            _id: templateId,
            name: 'Universal Resilience Maturity Assessment',
            industryType: 'Universal',
            questionCount: questions.length,
            questions: questions
          };
        }
      }
      
      // Set the template data (either from API or mock data)
      setSelectedTemplate(templateData);
      setModalVisible(true);
      
      // Position modal in the center of the screen
      setModalPosition({
        x: window.innerWidth / 2 - 400,
        y: window.innerHeight / 2 - 300
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error in view template process:', err);
      alert('Failed to load template details. Please try again.');
      setLoading(false);
    }
  };

  const handleDragStart = (e) => {
    if (e.target.classList.contains('modal-header') || e.target.classList.contains('drag-handle')) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleDragMove = (e) => {
    if (isDragging) {
      setModalPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
    } else {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging]);

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-header-with-back">
          <Link to="/admin" className="admin-back-button">
            <FaArrowLeft /> Back to Admin Dashboard
          </Link>
          <h1 className="admin-title">Assessment Template Management</h1>
        </div>
        <div className="admin-loading">Loading templates...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-header-with-back">
          <Link to="/admin" className="admin-back-button">
            <FaArrowLeft /> Back to Admin Dashboard
          </Link>
          <h1 className="admin-title">Assessment Template Management</h1>
        </div>
        <div className="admin-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header-with-back">
        <Link to="/admin" className="admin-back-button">
          <FaArrowLeft /> Back to Admin Dashboard
        </Link>
        <h1 className="admin-title">Assessment Template Management</h1>
      </div>
      
      <div className="admin-upload-container">
        <h2>Upload New Template</h2>
        {uploadMessage && (
          <div className={`admin-message ${uploadMessage.type === 'error' ? 'admin-error' : 'admin-success'}`}>
            {uploadMessage.text}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label htmlFor="templateName">Template Name</label>
            <input
              type="text"
              id="templateName"
              name="templateName"
              className="admin-form-control"
              value={uploadForm.templateName}
              onChange={handleInputChange}
              placeholder="e.g., Healthcare Resilience Maturity Assessment"
              required
            />
          </div>
          
          <div className="admin-form-group">
            <label htmlFor="industryType">Industry Type</label>
            <select
              id="industryType"
              name="industryType"
              className="admin-form-control"
              value={uploadForm.industryType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Industry Type</option>
              {industryTypes.map((industry, index) => (
                <option key={index} value={industry}>{industry}</option>
              ))}
            </select>
          </div>
          
          <div className="admin-form-group">
            <label htmlFor="templateFile">Template JSON File</label>
            <input
              type="file"
              id="templateFile"
              name="templateFile"
              className="admin-form-control"
              accept=".json"
              onChange={handleFileChange}
              required
            />
            <small>Upload a JSON file with the assessment questions and maturity levels.</small>
          </div>
          
          <button 
            type="submit" 
            className="admin-btn" 
            disabled={isUploading || !uploadForm.templateData}
          >
            {isUploading ? 'Uploading...' : 'Upload Template'}
          </button>
        </form>
      </div>
      
      <h2>Existing Templates</h2>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Industry Type</th>
              <th>Name</th>
              <th>Questions</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map(template => (
              <tr key={template._id}>
                <td>{template.industryType}</td>
                <td>{template.name}</td>
                <td>{template.questionCount}</td>
                <td>{new Date(template.updatedAt).toLocaleDateString()}</td>
                <td>
                  <div className="template-actions">
                    <button
                      className="btn-icon delete"
                      onClick={() => handleDeleteTemplate(template._id)}
                      title="Delete Template"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalVisible && selectedTemplate && (
        <div className="admin-modal-overlay" onClick={(e) => {
          // Close modal when clicking outside
          if (e.target.className === 'admin-modal-overlay') {
            setModalVisible(false);
          }
        }}>
          <div 
            className="admin-modal" 
            ref={modalRef}
            style={{ 
              left: `${modalPosition.x}px`, 
              top: `${modalPosition.y}px`,
              position: 'absolute',
              width: '800px',
              maxWidth: '90vw',
              maxHeight: '80vh',
              overflow: 'auto',
              backgroundColor: '#fff',
              borderRadius: '5px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
              zIndex: 1000
            }}
          >
            <div 
              className="modal-header" 
              onMouseDown={handleDragStart}
              style={{ 
                padding: '15px', 
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'move',
                backgroundColor: '#243444',
                color: '#fff',
                borderTopLeftRadius: '5px',
                borderTopRightRadius: '5px'
              }}
            >
              <div className="drag-handle" style={{ display: 'flex', alignItems: 'center' }}>
                <FaGripLines style={{ marginRight: '10px' }} /> 
                <h3 style={{ margin: 0 }}>{selectedTemplate.name} Template</h3>
              </div>
              <button 
                onClick={() => setModalVisible(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#fff', 
                  fontSize: '18px',
                  cursor: 'pointer' 
                }}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="template-info" style={{ marginBottom: '20px' }}>
                <p><strong>Industry Type:</strong> {selectedTemplate.industryType}</p>
                <p><strong>Questions:</strong> {selectedTemplate.questions ? selectedTemplate.questions.length : selectedTemplate.questionCount || 0}</p>
                <p><strong>Last Updated:</strong> {selectedTemplate.updatedAt ? new Date(selectedTemplate.updatedAt).toLocaleDateString() : new Date().toLocaleDateString()}</p>
                
                {selectedTemplate.description && (
                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <p><strong>Description:</strong></p>
                    <p style={{ fontSize: '0.9em' }}>{selectedTemplate.description}</p>
                  </div>
                )}
                
                {selectedTemplate.bestFor && (
                  <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <p><strong>Best For:</strong></p>
                    <p style={{ fontSize: '0.9em' }}>{selectedTemplate.bestFor}</p>
                  </div>
                )}
              </div>
              
              <h4 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '5px' }}>Assessment Questions</h4>
              
              {selectedTemplate.questions && selectedTemplate.questions.length > 0 ? (
                <div className="template-questions">
                  {selectedTemplate.questions.map((question, index) => (
                    <div key={index} className="template-question" style={{ 
                      marginBottom: '15px', 
                      padding: '15px', 
                      backgroundColor: '#f9f9f9',
                      borderRadius: '4px',
                      borderLeft: '3px solid #D2BA7B'
                    }}>
                      <div style={{ marginBottom: '10px' }}>
                        <strong>Q{index + 1}:</strong> {question.question || question.text}
                      </div>
                      
                      {question.description && (
                        <div style={{ marginLeft: '20px', marginBottom: '10px', fontSize: '0.9em', color: '#444' }}>
                          {question.description}
                        </div>
                      )}
                      
                      {question.standards && (
                        <div style={{ marginLeft: '20px', marginBottom: '10px' }}>
                          <strong>Standards:</strong> <span style={{ fontSize: '0.9em' }}>{question.standards}</span>
                        </div>
                      )}
                      
                      {question.maturityLevels && question.maturityLevels.length > 0 && (
                        <div style={{ marginLeft: '20px' }}>
                          <strong>Maturity Levels:</strong>
                          <div style={{ 
                            marginTop: '5px', 
                            maxHeight: '150px', 
                            overflowY: 'auto', 
                            padding: '5px', 
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px'
                          }}>
                            {question.maturityLevels.map((level, levelIndex) => (
                              <div key={levelIndex} style={{ 
                                marginBottom: '5px', 
                                padding: '5px', 
                                fontSize: '0.85em',
                                borderBottom: levelIndex < question.maturityLevels.length - 1 ? '1px solid #ddd' : 'none'
                              }}>
                                <strong>{level.description.split(':')[0]}:</strong> {level.description.split(':')[1] || level.description}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {question.options && (
                        <div style={{ marginLeft: '20px' }}>
                          <strong>Answer Options:</strong>
                          <ul style={{ marginTop: '5px' }}>
                            {question.options.map((option, optIndex) => (
                              <li key={optIndex}>
                                {option.text} {option.value && `(Value: ${option.value})`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {question.category && (
                        <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666', textAlign: 'right' }}>
                          <span style={{ 
                            backgroundColor: '#243444', 
                            color: '#FFFAFA', 
                            padding: '3px 8px', 
                            borderRadius: '12px',
                            fontSize: '0.8em'
                          }}>
                            {question.category}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="admin-no-data" style={{ padding: '20px', textAlign: 'center' }}>
                  <FaClipboardList style={{ fontSize: '2rem', color: '#D2BA7B', marginBottom: '10px' }} />
                  <p>No questions available for this template.</p>
                  <p style={{ fontSize: '0.9rem', color: '#666' }}>This template may be in development or questions have not been added yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;
